import { type Column } from "@tanstack/react-table";
import { ArrowDownUp } from "lucide-react";

import { cn } from "@/lib/utils";

interface SortableColumnHeaderProps<TData> {
  align?: "center" | "end" | "start";
  column: Column<TData, unknown>;
  label: string;
}

/**
 * Clickable table header that toggles the column's sort direction.
 *
 * Pair it with `enableMultiSort: false` + `enableSortingRemoval: false` so a
 * click cycles ascending / descending without a third "unsorted" state.
 */
export const SortableColumnHeader = <TData,>({
  align = "start",
  column,
  label,
}: SortableColumnHeaderProps<TData>) => {
  const sortDirection = column.getIsSorted();

  return (
    <button
      type="button"
      className={cn(
        "flex min-w-0 w-full items-center gap-2 text-left",
        "cursor-pointer transition-colors hover:text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        align === "center" && "justify-center text-center",
        align === "end" && "justify-end text-right"
      )}
      aria-pressed={Boolean(sortDirection)}
      onClick={column.getToggleSortingHandler()}
    >
      <span className="min-w-0 whitespace-normal wrap-break-word leading-tight">{label}</span>
      <ArrowDownUp
        className={cn(
          "size-3.5 shrink-0",
          sortDirection ? "text-primary-600" : "text-muted-foreground"
        )}
        aria-hidden="true"
      />
    </button>
  );
};

/** `aria-sort` value for a column header cell, or undefined when unsorted. */
export const getColumnSortAria = <TData,>(column: Column<TData, unknown>) => {
  const sortDirection = column.getIsSorted();

  if (sortDirection === "asc") {
    return "ascending";
  }

  if (sortDirection === "desc") {
    return "descending";
  }

  return undefined;
};
